import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface DeliveryBoy {
  id: string;
  name: string;
  phone: string;
  password: string;
  area: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  last_active: string | null;
}

interface CreateFormData {
  name: string;
  phone: string;
  password: string;
  area: string;
}

const INITIAL_FORM: CreateFormData = { name: '', phone: '', password: '', area: '' };

export default function DeliveryBoysPanel() {
  const [boys, setBoys] = useState<DeliveryBoy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateFormData>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchQ, setSearchQ] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const showToast = useCallback((text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3000);
  }, []);

  const loadBoys = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('delivery_boys')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setBoys(data as DeliveryBoy[]);
    setLoading(false);
  }, []);

  useEffect(() => { loadBoys(); }, [loadBoys]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.password.trim()) {
      showToast('Naam, phone aur password zaroori hai!', 'error');
      return;
    }
    if (form.phone.length < 10) {
      showToast('Phone number 10 digits ka hona chahiye', 'error');
      return;
    }
    if (form.password.length < 4) {
      showToast('Password kam se kam 4 characters ka hona chahiye', 'error');
      return;
    }

    setSubmitting(true);
    if (editingId) {
      const { error } = await supabase
        .from('delivery_boys')
        .update({
          name: form.name.trim(),
          phone: form.phone.trim(),
          password: form.password.trim(),
          area: form.area.trim() || null,
        })
        .eq('id', editingId);
      if (error) {
        showToast(error.message.includes('unique') ? 'Yeh phone number already registered hai' : 'Update karne mein error aaya', 'error');
      } else {
        showToast('Delivery boy update ho gaya!', 'success');
        setShowForm(false);
        setEditingId(null);
        setForm(INITIAL_FORM);
        loadBoys();
      }
    } else {
      const { error } = await supabase
        .from('delivery_boys')
        .insert({
          name: form.name.trim(),
          phone: form.phone.trim(),
          password: form.password.trim(),
          area: form.area.trim() || null,
          status: 'active',
        });
      if (error) {
        showToast(error.message.includes('unique') ? 'Yeh phone number already registered hai' : 'Account banane mein error aaya', 'error');
      } else {
        showToast('Delivery boy ka account ban gaya!', 'success');
        setShowForm(false);
        setForm(INITIAL_FORM);
        loadBoys();
      }
    }
    setSubmitting(false);
  };

  const handleToggleStatus = async (boy: DeliveryBoy) => {
    const newStatus = boy.status === 'active' ? 'inactive' : 'active';
    const { error } = await supabase
      .from('delivery_boys')
      .update({ status: newStatus })
      .eq('id', boy.id);
    if (!error) {
      setBoys(prev => prev.map(b => b.id === boy.id ? { ...b, status: newStatus } : b));
      showToast(`${boy.name} ko ${newStatus === 'active' ? 'active' : 'inactive'} kar diya`, 'info');
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('delivery_boys').delete().eq('id', id);
    if (!error) {
      setBoys(prev => prev.filter(b => b.id !== id));
      showToast('Account delete ho gaya', 'info');
      setDeleteConfirmId(null);
      if (viewId === id) setViewId(null);
    } else {
      showToast('Delete karne mein error aaya', 'error');
    }
  };

  const handleCopyDetails = async (boy: DeliveryBoy) => {
    const text = `Name: ${boy.name}\nPhone: ${boy.phone}\nPassword: ${boy.password}${boy.area ? `\nArea: ${boy.area}` : ''}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(boy.id);
      showToast('Details copy ho gaya!', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      showToast('Copy nahi ho paya, manually copy karo', 'error');
    }
  };

  const handleEdit = (boy: DeliveryBoy) => {
    setForm({ name: boy.name, phone: boy.phone, password: boy.password, area: boy.area ?? '' });
    setEditingId(boy.id);
    setShowForm(true);
    setViewId(null);
  };

  const filtered = boys.filter(b => {
    const q = searchQ.toLowerCase();
    return !q || b.name.toLowerCase().includes(q) || b.phone.includes(q) || (b.area ?? '').toLowerCase().includes(q);
  });

  const activeCount = boys.filter(b => b.status === 'active').length;
  const inactiveCount = boys.filter(b => b.status === 'inactive').length;

  const viewBoy = viewId ? boys.find(b => b.id === viewId) : null;

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toastMsg && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-2xl text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap ${
          toastMsg.type === 'success' ? 'bg-green-500' : toastMsg.type === 'error' ? 'bg-red-500' : 'bg-gray-800'
        }`}>
          <i className={toastMsg.type === 'success' ? 'ri-check-line' : toastMsg.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          {toastMsg.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <i className="ri-e-bike-2-line text-blue-600 text-lg"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-base">Delivery Boys</p>
            <p className="text-gray-400 text-xs">{boys.length} accounts registered</p>
          </div>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingId(null); setForm(INITIAL_FORM); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-user-add-line"></i>
          New Account
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center mx-auto mb-2">
            <i className="ri-team-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{boys.length}</p>
          <p className="text-gray-400 text-xs mt-0.5">Total</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center">
          <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center mx-auto mb-2">
            <i className="ri-checkbox-circle-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
          <p className="text-gray-400 text-xs mt-0.5">Active</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center">
          <div className="w-9 h-9 rounded-xl bg-red-400 flex items-center justify-center mx-auto mb-2">
            <i className="ri-close-circle-line text-white text-sm"></i>
          </div>
          <p className="text-2xl font-bold text-gray-900">{inactiveCount}</p>
          <p className="text-gray-400 text-xs mt-0.5">Inactive</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Naam, phone ya area se search karo..."
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
        />
      </div>

      {/* Create / Edit Form */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gray-900 flex items-center justify-center">
                  <i className="ri-user-add-line text-white text-sm"></i>
                </div>
                <p className="text-gray-900 font-bold">{editingId ? 'Account Edit Karo' : 'Naya Account Banao'}</p>
              </div>
              <button
                onClick={() => { setShowForm(false); setEditingId(null); setForm(INITIAL_FORM); }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
              {/* Name */}
              <div>
                <label className="text-gray-700 text-sm font-semibold block mb-1.5">
                  Naam <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Delivery boy ka naam"
                    maxLength={50}
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="text-gray-700 text-sm font-semibold block mb-1.5">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                    placeholder="10 digit phone number"
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                  />
                </div>
                <p className="text-gray-400 text-xs mt-1">Login ke liye yahi number use hoga</p>
              </div>

              {/* Password */}
              <div>
                <label className="text-gray-700 text-sm font-semibold block mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Kam se kam 4 characters"
                    maxLength={20}
                    className="w-full pl-9 pr-10 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>
              </div>

              {/* Area */}
              <div>
                <label className="text-gray-700 text-sm font-semibold block mb-1.5">
                  Area / Zone <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                  <input
                    type="text"
                    value={form.area}
                    onChange={e => setForm(p => ({ ...p, area: e.target.value }))}
                    placeholder="e.g. Sector 14, South Delhi"
                    maxLength={100}
                    className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 outline-none"
                  />
                </div>
              </div>

              {/* Info box */}
              <div className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-information-line text-gray-400 text-sm shrink-0 mt-0.5"></i>
                <p className="text-gray-500 text-xs leading-relaxed">
                  Delivery boy is phone number aur password se delivery portal mein login karega. 
                  Unhe apna phone number aur password bata do.
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingId(null); setForm(INITIAL_FORM); }}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-gray-900 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
                  ) : (
                    <><i className="ri-save-line"></i>{editingId ? 'Update Karo' : 'Account Banao'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewBoy && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <p className="text-gray-900 font-bold">Account Details</p>
              <button onClick={() => setViewId(null)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="px-5 py-5 space-y-4">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-2 py-3">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white text-2xl font-bold">
                  {viewBoy.name.charAt(0).toUpperCase()}
                </div>
                <p className="text-gray-900 font-bold text-lg">{viewBoy.name}</p>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${viewBoy.status === 'active' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {viewBoy.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2.5">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-gray-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Phone</p>
                    <p className="text-gray-900 font-semibold text-sm">{viewBoy.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-lock-line text-gray-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-400 text-xs">Password</p>
                    <p className="text-gray-900 font-semibold text-sm">{viewBoy.password}</p>
                  </div>
                </div>
                {viewBoy.area && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0">
                      <i className="ri-map-pin-line text-gray-500"></i>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Area</p>
                      <p className="text-gray-900 font-semibold text-sm">{viewBoy.area}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className="ri-calendar-line text-gray-500"></i>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Joined</p>
                    <p className="text-gray-900 font-semibold text-sm">
                      {new Date(viewBoy.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2 pt-1">
                {/* Copy Details Button */}
                <button
                  onClick={() => handleCopyDetails(viewBoy)}
                  className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border transition-all ${
                    copiedId === viewBoy.id
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <i className={copiedId === viewBoy.id ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                  {copiedId === viewBoy.id ? 'Copied!' : 'Name, Phone & Password Copy Karo'}
                </button>
                <button
                  onClick={() => handleEdit(viewBoy)}
                  className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-edit-line"></i>Edit Account
                </button>
                <button
                  onClick={() => handleToggleStatus(viewBoy)}
                  className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border ${
                    viewBoy.status === 'active'
                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : 'bg-green-50 text-green-700 border-green-200'
                  }`}
                >
                  <i className={viewBoy.status === 'active' ? 'ri-pause-circle-line' : 'ri-play-circle-line'}></i>
                  {viewBoy.status === 'active' ? 'Inactive Karo' : 'Activate Karo'}
                </button>
                {deleteConfirmId === viewBoy.id ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
                    <p className="text-red-700 text-sm font-semibold text-center">Pakka delete karna hai?</p>
                    <div className="flex gap-2">
                      <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs font-semibold cursor-pointer whitespace-nowrap">
                        Cancel
                      </button>
                      <button onClick={() => handleDelete(viewBoy.id)} className="flex-1 py-2 rounded-xl bg-red-500 text-white text-xs font-bold cursor-pointer whitespace-nowrap">
                        Haan, Delete Karo
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmId(viewBoy.id)}
                    className="w-full py-2.5 rounded-xl text-gray-400 hover:text-red-500 text-xs font-semibold flex items-center justify-center gap-1 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i>Delete Account
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
            <i className="ri-e-bike-2-line text-3xl text-gray-400"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">
              {searchQ ? 'Koi result nahi mila' : 'Koi delivery boy nahi hai abhi'}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              {searchQ ? 'Alag naam ya phone try karo' : 'Upar wale button se naya account banao'}
            </p>
          </div>
          {!searchQ && (
            <button
              onClick={() => { setShowForm(true); setEditingId(null); setForm(INITIAL_FORM); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>Pehla Account Banao
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(boy => (
            <div
              key={boy.id}
              className="bg-white rounded-2xl border border-gray-100 hover:border-gray-200 transition-all overflow-hidden"
            >
              <div className="flex items-center gap-3 p-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white text-lg font-bold shrink-0">
                  {boy.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-gray-900 font-bold text-sm">{boy.name}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      boy.status === 'active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'
                    }`}>
                      {boy.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                    <i className="ri-phone-line"></i>{boy.phone}
                  </p>
                  {boy.area && (
                    <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>{boy.area}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleCopyDetails(boy)}
                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${
                      copiedId === boy.id ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title="Details copy karo"
                  >
                    <i className={`text-sm ${copiedId === boy.id ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                  </button>
                  <button
                    onClick={() => setViewId(boy.id)}
                    className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                    title="Details dekho"
                  >
                    <i className="ri-eye-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => handleEdit(boy)}
                    className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
                    title="Edit karo"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => handleToggleStatus(boy)}
                    className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${
                      boy.status === 'active'
                        ? 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                        : 'bg-green-50 text-green-600 hover:bg-green-100'
                    }`}
                    title={boy.status === 'active' ? 'Inactive karo' : 'Active karo'}
                  >
                    <i className={`text-sm ${boy.status === 'active' ? 'ri-pause-circle-line' : 'ri-play-circle-line'}`}></i>
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 pb-3 flex items-center gap-4 text-[10px] text-gray-400 border-t border-gray-50 pt-2">
                <span className="flex items-center gap-1">
                  <i className="ri-key-line"></i>
                  Password: {boy.password}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {new Date(boy.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info card */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <p className="text-gray-700 font-bold text-sm mb-2 flex items-center gap-2">
          <i className="ri-lightbulb-flash-line text-amber-500"></i>
          Login Instructions
        </p>
        <ul className="space-y-1.5 text-gray-500 text-xs">
          <li className="flex items-start gap-2">
            <span className="text-gray-400 shrink-0">1.</span>
            <span>Delivery boy ka phone number aur password usse bata do</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 shrink-0">2.</span>
            <span>Woh <strong className="text-gray-700">/delivery</strong> page par jaake apna phone + password se login kar sakta hai</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400 shrink-0">3.</span>
            <span>Login ke baad woh assigned orders dekh aur update kar sakta hai</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
